import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import moment from 'moment';
import DatePicker from 'components/feature/DatePicker';
import {ButtonPrimary} from 'components/buttons';
import {
  BULK_EXPORT_PERIODS,
  BULK_EXPORT_PERIOD_OPTIONS,
  formatBulkExportRange,
  getBulkExportDateRange,
  getBulkExportMaxEndDate,
  isBulkExportRangeTooLong
} from 'helpers/constants/reports/bulkExport';

export const MENU_PROPS = {
  anchorOrigin: {vertical: 'bottom', horizontal: 'left'},
  transformOrigin: {vertical: 'top', horizontal: 'left'},
  getContentAnchorEl: null
};

const isRangeValid = (range) =>
  Boolean(range?.[0] && range?.[1]) &&
  !isBulkExportRangeTooLong(range[0], range[1]);

const PeriodPicker = ({labelId, period, customRange, timezone, onChange}) => {
  const [open, setOpen] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [pendingRange, setPendingRange] = useState(customRange);

  useEffect(() => {
    setShowCalendar(open && period === BULK_EXPORT_PERIODS.CUSTOM);
    setPendingRange(customRange);
  }, [open]);

  const selectedLabel = () => {
    const option = BULK_EXPORT_PERIOD_OPTIONS.find((o) => o.value === period);
    const range = formatBulkExportRange(
      ...getBulkExportDateRange(period, timezone, customRange)
    );
    return range ? `${option.label} (${range})` : option.label;
  };

  const handleOptionClick = (value) => {
    if (value === BULK_EXPORT_PERIODS.CUSTOM) {
      setShowCalendar(true);
      return;
    }
    onChange(value, customRange);
    setOpen(false);
  };

  const handleApply = () => {
    if (!isRangeValid(pendingRange)) return;
    onChange(BULK_EXPORT_PERIODS.CUSTOM, pendingRange);
    setOpen(false);
  };

  return (
    <div className='Say2EatDrop'>
      <FormControl variant='outlined' fullWidth>
        <Select
          labelId={labelId}
          multiple
          value={[period]}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onChange={() => {}}
          renderValue={selectedLabel}
          MenuProps={MENU_PROPS}
        >
          <div className='DropDown DropFilterDate'>
            <div className='DropDownHolder'>
              <div className='DropDownList'>
                <List
                  component='nav'
                  className='DropDownMenu'
                  aria-label='Time period'
                >
                  {BULK_EXPORT_PERIOD_OPTIONS.map((option) => (
                    <ListItem
                      key={option.value}
                      button
                      selected={
                        showCalendar
                          ? option.value === BULK_EXPORT_PERIODS.CUSTOM
                          : option.value === period
                      }
                      onClick={() => handleOptionClick(option.value)}
                    >
                      <ListItemText primary={option.label} />
                    </ListItem>
                  ))}
                </List>
              </div>
              {showCalendar && (
                <div className='DropDownDatepicker'>
                  <DatePicker
                    setSelectedDate={() => {}}
                    dateValue={pendingRange}
                    datepickerOnChange={setPendingRange}
                    reportPartialRange
                    disableFuture
                    maxDate={
                      pendingRange?.[0] && !pendingRange?.[1]
                        ? moment(
                            getBulkExportMaxEndDate(pendingRange[0])
                          ).toDate()
                        : undefined
                    }
                  />
                  <div className='DropDownActions'>
                    <ButtonPrimary
                      className='ButtonSmall'
                      disabled={!isRangeValid(pendingRange)}
                      onClick={handleApply}
                    >
                      <span>Apply</span>
                    </ButtonPrimary>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Select>
      </FormControl>
    </div>
  );
};

PeriodPicker.propTypes = {
  labelId: PropTypes.string.isRequired,
  period: PropTypes.string.isRequired,
  customRange: PropTypes.arrayOf(PropTypes.string).isRequired,
  timezone: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired
};

PeriodPicker.defaultProps = {
  timezone: 0
};

export default PeriodPicker;
